import { Link } from "react-router-dom";
import Spinner from "./Spinner/Spinner";

// Every interactive control on the site should come from here so padding,
// height and focus states stay consistent. Sizes keep a >=44px touch target.
// Heights use arbitrary values because numeric `min-h-*` utilities only exist
// from Tailwind 3.4 and this project is on 3.3.5.
const SIZES = {
  sm: "min-h-[2.75rem] px-4 py-2 text-sm gap-1.5",
  md: "min-h-[3rem] px-6 py-2.5 text-base gap-2",
  lg: "min-h-[3.5rem] px-8 py-3 text-base gap-2",
  icon: "h-12 w-12 p-0",
  iconSm: "h-11 w-11 p-0",
};

const VARIANTS = {
  primary:
    "bg-slate-900 text-white enabled:hover:bg-slate-950 focus-visible:outline-slate-900",
  secondary:
    "border border-slate-900 bg-transparent text-slate-950 enabled:hover:bg-slate-900 enabled:hover:text-white focus-visible:outline-slate-900",
  ghost:
    "bg-transparent text-slate-700 enabled:hover:bg-slate-100 focus-visible:outline-slate-900",
  danger:
    "bg-red-600 text-white enabled:hover:bg-red-700 focus-visible:outline-red-600",
  dangerOutline:
    "border border-red-300 bg-transparent text-red-700 enabled:hover:bg-red-50 focus-visible:outline-red-600",
  // For dark backgrounds (footer, CTA bands)
  inverse:
    "bg-white text-slate-950 enabled:hover:bg-slate-200 focus-visible:outline-white",
  inverseOutline:
    "border border-white/30 bg-transparent text-white enabled:hover:bg-white enabled:hover:text-slate-950 focus-visible:outline-white",
};

const BASE =
  "inline-flex items-center justify-center rounded-full font-semibold leading-none transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 disabled:cursor-not-allowed disabled:opacity-40";

const Button = ({
  children,
  className = "",
  variant = "primary",
  size = "md",
  type = "button",
  fullWidth = false,
  disabled = false,
  loading = false,
  onClick,
  url,
  spinnerSpeed,
  ...rest
}) => {
  const classNames = [
    BASE,
    SIZES[size] || SIZES.md,
    VARIANTS[variant] || VARIANTS.primary,
    fullWidth ? "w-full" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const content = (
    <>
      {loading && (
        <Spinner
          size={size === "sm" || size === "iconSm" ? 18 : 22}
          color={
            variant === "primary" || variant === "danger" ? "#FFF" : "#0f172a"
          }
          speed={spinnerSpeed}
        />
      )}
      {children}
    </>
  );

  // A `url` renders a real anchor so links stay middle-clickable and crawlable.
  if (url && !disabled && !loading) {
    const isExternal = /^(https?:|mailto:|tel:)/.test(url);

    if (isExternal) {
      return (
        <a href={url} className={classNames} {...rest}>
          {content}
        </a>
      );
    }

    return (
      <Link to={url} className={classNames} {...rest}>
        {content}
      </Link>
    );
  }

  return (
    <button
      type={type}
      onClick={onClick}
      className={classNames}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {content}
    </button>
  );
};

export default Button;
